import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { videos } from "@/mocks/videos";
import VideoCard from "@/components/base/VideoCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Pagination from "@/components/feature/Pagination";
import usePageMeta from "@/hooks/usePageMeta";
import { daysAgo } from "@/utils/format";

const PAGE_SIZE = 12;

const timeOptions = [
  { value: "all", label: "全部时间" },
  { value: "7d", label: "最近 7 天" },
  { value: "30d", label: "最近 30 天" },
];

const sortOptions = [
  { value: "newest", label: "最新发布" },
  { value: "hot", label: "播放最多" },
];

export default function Category() {
  const { id } = useParams<{ id: string }>();
  const category = categories.find((cat) => cat.id === id);

  const [timeFilter, setTimeFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);

  usePageMeta({
    title: category ? `${category.name} - 黑料视频` : "未找到分类 - 黑料视频",
    description: category
      ? `${category.description} 黑料视频为你持续更新${category.name}相关的视频资讯。`
      : "抱歉，未找到对应的视频分类。",
    keywords: category
      ? `${category.name},黑料视频,娱乐八卦,视频资讯`
      : "黑料视频,娱乐八卦",
  });

  const categoryNameMap: Record<string, string> = categories.reduce(
    (acc, cat) => ({ ...acc, [cat.id]: cat.name }),
    {},
  );

  const allVideos = useMemo(
    () => videos.filter((video) => video.categoryId === id),
    [id],
  );

  const filtered = useMemo(() => {
    let list = [...allVideos];
    if (timeFilter === "7d") list = list.filter((v) => daysAgo(v.publishDate) <= 7);
    if (timeFilter === "30d") list = list.filter((v) => daysAgo(v.publishDate) <= 30);
    list.sort((a, b) =>
      sort === "hot" ? b.views - a.views : a.publishDate < b.publishDate ? 1 : -1,
    );
    return list;
  }, [allVideos, timeFilter, sort]);

  useEffect(() => {
    setPage(1);
  }, [timeFilter, sort, id]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => {
    setTimeFilter("all");
    setSort("newest");
    setPage(1);
  };

  if (!category) {
    return (
      <div className="mx-auto w-full max-w-[1400px] px-4 py-20 md:px-6">
        <div className="flex flex-col items-center justify-center rounded-lg border border-background-200 bg-background-100 px-6 py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-error-warning-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-xl font-black text-foreground-950">
            没有找到这个分类
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            它可能已被合并或移动，去看看其他热门栏目吧。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
            <Link
              to="/category/star-latest"
              className="rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-600 cursor-pointer whitespace-nowrap"
            >
              浏览明星动态
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "视频分类", to: "/category/star-latest" },
              { label: category.name },
            ]}
          />
          <div className="mt-5 flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className={`${category.icon} text-2xl`}></i>
            </span>
            <div>
              <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                {category.name}
              </h1>
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
                {category.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6">
          {/* Filter bar */}
          <div className="mb-6 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <span className="flex items-center gap-1 text-xs font-medium text-foreground-500">
                <i className="ri-calendar-line"></i>
                上传时间
              </span>
              <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
                {timeOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setTimeFilter(opt.value)}
                    className={`rounded-full px-3 py-1 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      timeFilter === opt.value
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <span className="flex items-center gap-1 text-xs font-medium text-foreground-500">
                <i className="ri-fire-line"></i>
                排序
              </span>
              <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSort(opt.value)}
                    className={`rounded-full px-3 py-1 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      sort === opt.value
                        ? "bg-secondary-500 text-background-50"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <p className="mb-4 text-sm text-foreground-500">
            共找到 <span className="font-medium text-primary-600">{filtered.length}</span> 条视频
          </p>

          {pageItems.length > 0 ? (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {pageItems.map((video) => (
                  <VideoCard
                    key={video.id}
                    video={video}
                    categoryName={categoryNameMap[video.categoryId]}
                  />
                ))}
              </div>
              <Pagination current={currentPage} total={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-background-200 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-inbox-archive-line text-3xl"></i>
              </span>
              <h2 className="mt-5 font-heading text-lg font-black text-foreground-950">
                当前筛选条件下没有视频
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                试试放宽时间范围，或切换为「全部时间」重新查看。
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-6 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
              >
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}